import angular from 'angular';
import _ from 'lodash';
import logger from '../../../shared/logger';
import { EMPLOYER_WORKFLOW_STATUS } from '../../employer/constants/employer-workflow';
import { RECRUITER_WORKFLOW_STATUS } from '../../recruiter/constants/recruiter-workflow';

export const eventTypes = {
    RESUME_CREATED: 'resumeCreated',
    CANDIDATE_APPROVED_RESUME: 'candidateApprovedResume',
    TALENT_EXPERT_SENT_RESUME_TO_CANDIDATE: 'talentExpertSentResumeToCandidate',
    INTERVIEW_CREATED: 'interviewCreated',
    REQUEST_FEEDBACK: 'requestFeedback',
    BLOCK_REQUEST_FEEDBACK: 'blockRequestFeedback',
    TALENT_RESUME_FOR_EMPLOYER: 'talentResumeForEmployer',
    TALENT_EXPERT_CONTACT_CANDIDATE: 'talentExpertContactCandidate',
    EMPLOYER_CONTACT_CANDIDATE: 'employerContactCandidate',
    TALENYA_BOT_CONTACT_CANDIDATE: 'botContactCandidate',
    CONTACT_CANDIDATE: 'contactCandidate',
    CONFIRM_RESPONSE: 'confirmResponse',
};

export const engagedSteps = {
    RESUME_CREATED: 'resumeCreated',
    RESUME_APPROVED: 'resumeApproved',
    RESUME_SENT: 'resumeSent',
    NONE: 'none',
};

export const WorkflowService = (Restangular) => {
    const getWorkflowSearch = function (data) {
        if (_.isEmpty(data)) {
            // eslint-disable-next-line no-undef
            return new Promise(((resolve, reject) => {
                reject('Missing Data');
            }));
        }
        return Restangular.all('workflow')
            .all('search')
            .post(data)
            .catch(err => logger.error(err));
    };

    const getEventsWorkflow = function (id) {
        return Restangular.all('event')
            .one('workflow', id)
            .get()
            .catch(err => logger.error(err));
    };

    const postWorkflow = function (data) {
        return Restangular.all('workflow')
            .post(data)
            .catch(err => logger.error(err));
    };

    const putRecruiterWorkflowStatus = function (id, data) {
        return Restangular.one('workflow', id)
            .all('recruiterStatus')
            .customPUT(data)
            .catch(err => logger.error(err));
    };

    const putEmployerWorkflowStatus = function (id, data) {
        return Restangular.one('workflow', id)
            .all('employerStatus')
            .customPUT(data)
            .catch(err => logger.error(err));
    };

    const moveWorkflowsToFullService = function (workflowIds) {
        return Restangular.one('workflow')
            .all('moveWorkflowsToFullService')
            .post(workflowIds)
            .catch(err => logger.error(err));
    };

    const postWorkflowEvent = function (workflow_id, event) {
        return Restangular.one('workflow', workflow_id)
            .all('event')
            .post(event)
            .catch(err => logger.error(err));
    };

    const postWorkflowCountersPaginated = function (params) {
        return Restangular.all('workflow')
            .all('countersPaginated')
            .post(params)
            .catch(err => logger.error(err));
    };

    const bulkRejectOnRecruiterSide = function (data) {
        return Restangular
            .all('workflow')
            .all('bulkRejectOnRecruiterSide')
            .post(data)
            .catch(error => logger.error(error));
    };

    const bulkClaimOnRecruiterSide = function (data) {
        return Restangular
            .all('workflow')
            .all('bulkClaimOnRecruiterSide')
            .post(data)
            .catch(error => logger.error(error));
    };

    const getCandidatesEmployerWorkflowStatus = function (positionId, workflowIds) {
        return Restangular
            .one('position', positionId)
            .all('workflow')
            .all('employerWorkflowStatus')
            .post({ workflowIds })
            .catch(error => logger.error(error));
    };

    const getCandidateConsent = function (workflowId) {
        return Restangular
            .one('workflow', workflowId)
            .all('candidateConsents')
            .get('')
            .catch(error => logger.error(error));
    };

    const getCandidateWorkflowEngagedStep = (workflow) => {
        const resumeSentToEmployerIndex = _.findIndex(workflow.events, {
            eventType: eventTypes.RESUME_CREATED,
        });
        const resumeApprovedIndex = _.findIndex(workflow.events, {
            eventType: eventTypes.CANDIDATE_APPROVED_RESUME,
        });
        const talentExpertSentResumeToCandidateIndex = _.findIndex(workflow.events, {
            eventType: eventTypes.TALENT_EXPERT_SENT_RESUME_TO_CANDIDATE,
        });

        const isStepNone = resumeSentToEmployerIndex < 0;

        if (isStepNone) {
            return engagedSteps.NONE;
        }

        const isStepResumeApproved = resumeApprovedIndex >= 0 &&
            (
                (talentExpertSentResumeToCandidateIndex >= 0 &&
                    resumeApprovedIndex < resumeSentToEmployerIndex &&
                    resumeApprovedIndex < talentExpertSentResumeToCandidateIndex) ||
                (talentExpertSentResumeToCandidateIndex < 0 &&
                    resumeApprovedIndex < resumeSentToEmployerIndex)
            );

        if (isStepResumeApproved) {
            return engagedSteps.RESUME_APPROVED;
        }

        const isStepResumeSent = talentExpertSentResumeToCandidateIndex >= 0 &&
            talentExpertSentResumeToCandidateIndex < resumeSentToEmployerIndex;

        if (isStepResumeSent) {
            return engagedSteps.RESUME_SENT;
        }

        return engagedSteps.RESUME_CREATED;
    };

    const isFeedbackFromEmployerExists = feedbackStatus => feedbackStatus === 'accepted';

    const getFeedbackWorkflowCounters = ({ workflows = [] } = {}) => {
        const all = workflows.length;

        const { rated, nonRated } = workflows.reduce((accumulator, workflow) => {
            _.get(workflow, 'feedback.grades.0.grade') != null ? accumulator.rated++ : accumulator.nonRated++;
            return accumulator;
        }, {
            rated: 0,
            nonRated: 0,
        });

        return {
            all,
            rated,
            nonRated,
        };
    };

    const getValidRejectionReason = ({ workflow }) => {
        const employerStatus = _.get(workflow, 'employerStatus.current');
        const recruiterStatus = _.get(workflow, 'recruiterStatus.current');
        const employerReason = _.get(workflow, 'employerStatus.reason');
        const recruiterReason = _.get(workflow, 'recruiterStatus.reason');
        if (employerStatus === EMPLOYER_WORKFLOW_STATUS.REJECTED && employerReason && employerReason !== 'N/A') {
            return employerReason;
        }

        if (recruiterStatus === RECRUITER_WORKFLOW_STATUS.REJECTED && recruiterStatus && recruiterStatus !== 'N/A') {
            return recruiterReason;
        }

        return employerStatus === EMPLOYER_WORKFLOW_STATUS.REJECTED ? employerReason : recruiterReason;
    };

    const isAutoContactEventExist = workflow => !!_.chain(workflow)
        .get('events', [])
        .find(event => [
            eventTypes.TALENT_EXPERT_CONTACT_CANDIDATE,
            eventTypes.TALENYA_BOT_CONTACT_CANDIDATE,
        ].includes(_.get(event, 'eventType')))
        .value();


    return {
        isAutoContactEventExist,
        getFeedbackWorkflowCounters,
        getValidRejectionReason,
        getWorkflowSearch,
        getEventsWorkflow,
        postWorkflow,
        putRecruiterWorkflowStatus,
        putEmployerWorkflowStatus,
        moveWorkflowsToFullService,
        postWorkflowEvent,
        postWorkflowCountersPaginated,
        bulkRejectOnRecruiterSide,
        bulkClaimOnRecruiterSide,
        getCandidateWorkflowEngagedStep,
        isFeedbackFromEmployerExists,
        getCandidatesEmployerWorkflowStatus,
        getCandidateConsent,
        eventTypes,
        engagedSteps,
    };
};

angular
    .module('TALENYA-FE')
    .factory('WorkflowService', WorkflowService);
